import { useEffect, useRef } from 'react'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { menuCodeHeaders } from '@/utils/menuCodeHeaders'
import { BIZEST_CANCEL_RESULT_DOWNLOAD_URL } from '@/services/outbound/bizestCancelResult/apis'
import { BIZEST_CANCEL_RESULT_MENU_CODE } from '@/services/outbound/bizestCancelResult/consts'
import type { BizestCancelResultDownloadReq } from '@/services/outbound/bizestCancelResult/types'
import BizestCancelResultPage from './page.generated'
import {
  bindBizestCancelDownload,
  bindBizestCancelPersist,
  fromSearchQuery,
  initBizestCancelStore,
  saveSearchLists,
  toUrlQuery,
  withSavedLists,
} from './wiring'

const DOWNLOAD_CONFIG = { method: 'post', ...menuCodeHeaders(BIZEST_CANCEL_RESULT_MENU_CODE) } as const

/**
 * GI17 출고취소(비제스트) 2.0 — 화면은 컨덕터 생성본(page.generated.tsx), 배선은 wiring.ts (LOGISPRD-16035).
 * 조회 전용이다. 1.0 GI17(iframe)은 그대로 두고 새 메뉴코드로 나란히 올린다.
 */
export default function BizestCancelResult() {
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  // reload 는 useCallback 이라 참조가 고정이다. 컨텍스트 값 객체는 Provider 렌더마다 새로 만들어진다.
  const { reload: reloadMenuSession } = useMenuOnSession()
  // 모듈 상태(조회 조건)는 첫 렌더 전에 정한다. 다른 탭에 다녀오면 URL 에 실어 둔 조건으로 다시 조회하고(LOGISPRD-17344),
  // 탭을 닫았다 다시 열면 URL 이 비어 빈 화면에서 시작한다.
  // 진입 한 번만 URL 을 읽는다. 진입 뒤에는 [조회] 가 조건을 정하고 URL 은 그 결과를 따라 적을 뿐이다(URL 이 조회를 다시 걸지 않는다).
  const initialized = useRef(false)
  if (!initialized.current) {
    initialized.current = true
    initBizestCancelStore(withSavedLists(fromSearchQuery(parsedQs)))
  }
  useEffect(() => {
    bindBizestCancelPersist((req) => {
      // 주문번호 목록은 URL 한도 때문에 sessionStorage 에 따로 둔다(wiring saveSearchLists)
      saveSearchLists(req)
      const query = req ? toUrlQuery(req) : {}
      MenuStorage.menuOnSession.setSearch(query)
      replaceQuery(query)
      reloadMenuSession()
    })
    return () => bindBizestCancelPersist(null)
  }, [reloadMenuSession, replaceQuery])
  // 엑셀 다운로드는 BE 비밀번호 zip(LOGISPRD-17358). 실패 안내는 훅이 새 디자인 알럿으로 띄운다.
  const { onDownloadExcel } = useExcelDownload<BizestCancelResultDownloadReq>({
    fileNamePrefix: '출고취소(비제스트)',
    config: DOWNLOAD_CONFIG,
    errorToast: true,
  })
  useEffect(() => {
    bindBizestCancelDownload((body) => onDownloadExcel(BIZEST_CANCEL_RESULT_DOWNLOAD_URL, body))
    return () => bindBizestCancelDownload(null)
  }, [onDownloadExcel])
  return <BizestCancelResultPage />
}
